// src/components/artwork/OcrEnrichButton.jsx
//
// Bouton "Enrichir via OCR" sur la fiche œuvre (ArtworkDetail)
//
// Types de documents reconnus (depuis la colonne documents de artworks) :
//   "invoice"          → source pour dimensions, prix, artiste, titre, année, medium
//   "certificate"      → source UNIQUEMENT pour signature / authentication_details / signature_notes
//   "condition_report" → IGNORÉ (constat d'état, pas de données fiables pour enrichissement)
//   "other"            → IGNORÉ
//
// Logique double analyse :
//   1. Facture analysée en premier → données de base + dimensions de référence
//   2. Certificat analysé ensuite  → champs signature uniquement
//   3. Si ni facture ni certificat → fallback sur premier doc disponible (hors condition_report/other)
//
// Détection écart dimensions facture vs fiche :
//   - Écart symétrique 2-25cm → probable cadre inclus dans la fiche
//   - Confirmation obligatoire avant d'appliquer les dimensions

import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2, Sparkles, Check, X, AlertTriangle } from "lucide-react";
import { toast } from "react-toastify";
import { supabase } from "../../lib/supabaseClient";
import { mapOcrToArtworkPatch } from "../../utils/mapOcrToArtworkPatch";
import AiDisclosure from "../AiDisclosure";
import { VISION_API_URL } from "../../config/domains";

const OCR_API = `${VISION_API_URL}/analyze-document`;

// Champs enrichissables et leur source OCR
const FIELDS = [
  { key: "year", labelKey: "ocrEnrich.fields.year", fallbackLabel: "Année", ocr: "year" },
  { key: "dimensions", labelKey: "ocrEnrich.fields.dimensions", fallbackLabel: "Dimensions", ocr: "dimensions" },
  { key: "medium", labelKey: "ocrEnrich.fields.medium", fallbackLabel: "Medium / technique", ocr: "medium_clean" },
  { key: "medium_category", labelKey: "ocrEnrich.fields.mediumCategory", fallbackLabel: "Catégorie", ocr: "category" },
  { key: "acquisition_price", labelKey: "ocrEnrich.fields.acquisitionPrice", fallbackLabel: "Prix d'acquisition", ocr: "price" },
  { key: "acquisition_type", labelKey: "ocrEnrich.fields.acquisitionType", fallbackLabel: "Type d'acquisition", ocr: "acquisition_type" },
  { key: "acquisition_date", labelKey: "ocrEnrich.fields.acquisitionDate", fallbackLabel: "Date d'acquisition", ocr: "acquisition_date" },
  { key: "seller", labelKey: "ocrEnrich.fields.seller", fallbackLabel: "Donateur / vendeur", ocr: "seller",  group: "acquisition" },
  { key: "buyer", labelKey: "ocrEnrich.fields.buyer", fallbackLabel: "Bénéficiaire / acquéreur", ocr: "buyer",  group: "acquisition" },
  { key: "title", labelKey: "ocrEnrich.fields.title", fallbackLabel: "Titre", ocr: "title" },
  { key: "artist", labelKey: "ocrEnrich.fields.artist", fallbackLabel: "Artiste", ocr: "artist" },
  { key: "signature_details", labelKey: "ocrEnrich.fields.signature", fallbackLabel: "Signature", ocr: "signature" },
  { key: "authentication_details", labelKey: "ocrEnrich.fields.authentication", fallbackLabel: "Certificat / auth.", ocr: "certificate_reference" },
  { key: "signature_notes", labelKey: "ocrEnrich.fields.signatureNotes", fallbackLabel: "Notes signature", ocr: "authentication_notes" },
  // Champs maison de vente
  { key: "auction_house", labelKey: "ocrEnrich.fields.auctionHouse", fallbackLabel: "Maison de vente", ocr: "auction_house",         group: "auction" },
  { key: "auction_sale_name", labelKey: "ocrEnrich.fields.auctionSaleName", fallbackLabel: "Nom de la vente", ocr: "auction_sale_name",     group: "auction" },
  { key: "auction_sale_date", labelKey: "ocrEnrich.fields.auctionSaleDate", fallbackLabel: "Date de la vente", ocr: "auction_sale_date",     group: "auction" },
  { key: "auction_lot_number", labelKey: "ocrEnrich.fields.auctionLotNumber", fallbackLabel: "Numéro de lot", ocr: "auction_lot_number",    group: "auction" },
  { key: "auction_hammer_price", labelKey: "ocrEnrich.fields.auctionHammerPrice", fallbackLabel: "Prix adjugé (€)", ocr: "auction_hammer_price",  group: "auction" },
  { key: "auction_fees", labelKey: "ocrEnrich.fields.auctionFees", fallbackLabel: "Frais de vente TTC", ocr: "auction_fees",          group: "auction" },
];

// Champs alimentés UNIQUEMENT par le certificat
const CERT_ONLY_FIELDS = ["signature_details", "authentication_details", "signature_notes"];

// Types à ignorer complètement pour l'enrichissement
const IGNORED_TYPES = ["condition_report"];

const TECHNICAL_TERMS = [
  "action painting", "acrylic", "oil", "watercolor", "painting",
  "sculpture", "drawing", "canvas", "toile", "huile", "acrylique",
  "aquarelle", "gouache", "pastel", "gravure", "lithographie"
];


// medium_category = famille artistique large (taxonomie)
// medium = technique précise canonique ou libellé technique si inconnue
const MEDIUM_CATEGORY_LABELS_FR = {
  painting: "Peinture",
  photography: "Photographie",
  sculpture: "Sculpture",
  print: "Estampe",
  drawing: "Dessin",
  work_on_paper: "Œuvre sur papier",
  mixed_media: "Technique mixte",
  installation: "Installation",
  film_video: "Film / Vidéo",
  digital: "Art numérique",
  ceramics: "Céramique",
  textile: "Textile",
  design: "Design",
  other: "Autre",
};

const MEDIUM_LABELS_FR = {
  oil_on_canvas: "Huile sur toile",
  acrylic_on_canvas: "Acrylique sur toile",
  oil_on_paper: "Huile sur papier",
  acrylic_on_paper: "Acrylique sur papier",
  ink_on_paper: "Encre sur papier",
  pencil_on_paper: "Crayon sur papier",
  graphite_on_paper: "Mine de plomb sur papier",
  charcoal_on_paper: "Fusain sur papier",
  watercolor_on_paper: "Aquarelle sur papier",
  gouache_on_paper: "Gouache sur papier",
  pastel_on_paper: "Pastel sur papier",
  mixed_media: "Technique mixte",
  lambda_print: "Tirage Lambda",
  c_print: "C-print",
  gelatin_silver_print: "Tirage argentique",
  pigment_print: "Tirage pigmentaire",
  bronze: "Bronze",
};

const CANONICAL_MEDIUM_RULES = [
  { key: "oil_on_canvas", category: "painting", terms: ["oil_on_canvas", "huile sur toile", "oil on canvas", "öl auf leinwand"] },
  { key: "acrylic_on_canvas", category: "painting", terms: ["acrylic_on_canvas", "acrylique sur toile", "acrylic on canvas"] },
  { key: "oil_on_paper", category: "work_on_paper", terms: ["oil_on_paper", "huile sur papier", "oil on paper"] },
  { key: "acrylic_on_paper", category: "work_on_paper", terms: ["acrylic_on_paper", "acrylique sur papier", "acrylic on paper"] },
  { key: "ink_on_paper", category: "work_on_paper", terms: ["ink_on_paper", "encre sur papier", "lavis d'encre", "ink wash", "ink on paper"] },
  { key: "pencil_on_paper", category: "work_on_paper", terms: ["pencil_on_paper", "crayon sur papier", "pencil on paper"] },
  { key: "graphite_on_paper", category: "work_on_paper", terms: ["graphite_on_paper", "mine de plomb", "graphite on paper"] },
  { key: "charcoal_on_paper", category: "work_on_paper", terms: ["charcoal_on_paper", "fusain sur papier", "charcoal on paper"] },
  { key: "watercolor_on_paper", category: "work_on_paper", terms: ["watercolor_on_paper", "aquarelle sur papier", "watercolor on paper", "watercolour on paper"] },
  { key: "gouache_on_paper", category: "work_on_paper", terms: ["gouache_on_paper", "gouache sur papier", "gouache on paper"] },
  { key: "pastel_on_paper", category: "work_on_paper", terms: ["pastel_on_paper", "pastel sur papier", "pastel on paper"] },
  { key: "mixed_media", category: "mixed_media", terms: ["mixed_media", "technique mixte", "mixed media"] },
  { key: "lambda_print", category: "photography", terms: ["lambda_print", "tirage lambda", "lambda print"] },
  { key: "c_print", category: "photography", terms: ["c_print", "c-print", "c print", "chromogenic", "chromogène"] },
  { key: "gelatin_silver_print", category: "photography", terms: ["gelatin_silver_print", "tirage argentique", "gelatin silver", "silver gelatin"] },
  { key: "pigment_print", category: "photography", terms: ["pigment_print", "tirage pigmentaire", "pigment print", "inkjet print"] },
  { key: "bronze", category: "sculpture", terms: ["bronze"] },
];

// ── Utilitaires ────────────────────────────────────────────────────────────

function parseDocuments(docs) {
  if (!docs) return [];
  try {
    const arr = typeof docs === "string" ? JSON.parse(docs) : docs;
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}


function isAcquisitionLikeDocument(doc) {
  const text = normalizeComparableText(
    `${doc?.name || ""} ${doc?.type || ""} ${doc?.storagePath || ""} ${doc?.file || ""} ${doc?.url || ""}`
  );

  return (
    doc?.type === "invoice" ||
    text.includes("facture") ||
    text.includes("bordereau") ||
    text.includes("donation") ||
    text.includes("acte de donation") ||
    text.includes("echange") ||
    text.includes("échange") ||
    text.includes("acte d echange")
  );
}

function scoreArtworkMatch(candidate, artwork) {
  if (!candidate || !artwork) return 0;

  let score = 0;

  const candidateText = buildSourceText(candidate);
  const artworkTitle = normalizeComparableText(artwork.title);
  const artworkArtist = normalizeComparableText(artwork.artist);

  if (artworkTitle && candidateText.includes(artworkTitle)) score += 80;
  if (artworkArtist && candidateText.includes(artworkArtist)) score += 20;

  const candidateDims = parseDimStr(candidate.dimensions || candidateText);
  const artworkDims = parseDimStr(artwork.dimensions);

  if (candidateDims && artworkDims) {
    const sameDirect =
      Math.abs(candidateDims.h - artworkDims.h) <= 3 &&
      Math.abs(candidateDims.w - artworkDims.w) <= 3;

    const sameReversed =
      Math.abs(candidateDims.h - artworkDims.w) <= 3 &&
      Math.abs(candidateDims.w - artworkDims.h) <= 3;

    if (sameDirect || sameReversed) score += 60;
  }

  const candidatePrice = normalizePrice(
    candidate.price ||
    candidate.acquisition_price ||
    candidate.value ||
    candidate.declared_value
  );

  const artworkPrice = normalizePrice(
    artwork.acquisition_price ||
    artwork.price ||
    artwork.acquisitionDetails?.price ||
    artwork.acquisitionDetails?.declared_value
  );

  if (candidatePrice !== null && artworkPrice !== null) {
    const ratio = Math.abs(candidatePrice - artworkPrice) / Math.max(candidatePrice, artworkPrice);
    if (ratio <= 0.03) score += 50;
  }

  return score;
}

function pickBestArtworkMatch(artworksList, artwork) {
  if (!Array.isArray(artworksList) || !artworksList.length) return null;
  if (artworksList.length === 1) return artworksList[0];

  const ranked = artworksList
    .map((candidate) => ({
      candidate,
      score: scoreArtworkMatch(candidate, artwork),
    }))
    .sort((a, b) => b.score - a.score);

  console.log("[OCR artwork match ranked]", ranked.map(({ candidate, score }) => ({
    score,
    title: candidate.title,
    dimensions: candidate.dimensions,
    price: candidate.price || candidate.value || candidate.declared_value,
  })));

  return ranked[0]?.score >= 40 ? ranked[0].candidate : null;
}

function normalizeComparableText(value) {
  return String(value || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[’']/g, "'")
    .replace(/[_-]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function normalizeDimensionsForCompare(value) {
  const parsed = parseDimStr(value);
  if (!parsed) {
    return normalizeComparableText(value)
      .replace(/\s/g, "")
      .replace(/×/g, "x")
      .replace(/cm\b/g, "");
  }

  const depthMatch = String(value || "").match(
    /\d+[.,]?\d*\s*[x×]\s*\d+[.,]?\d*\s*[x×]\s*(\d+[.,]?\d*)/i
  );
  const d = depthMatch ? parseFloat(depthMatch[1].replace(",", ".")) : null;
  const parts = [parsed.h, parsed.w, d]
    .filter((n) => n !== null && !Number.isNaN(n))
    .map((n) => String(Number(n.toFixed(3))).replace(/\.0+$/, ""));

  return parts.join("x") + "cm";
}

function canonicalMedium(value) {
  const normalized = normalizeComparableText(value);
  if (!normalized) return null;

  for (const rule of CANONICAL_MEDIUM_RULES) {
    if (rule.terms.some((term) => normalized.includes(normalizeComparableText(term)))) {
      return rule.key;
    }
  }

  return null;
}

function inferMediumCategory(value) {
  const normalized = normalizeComparableText(value);
  if (!normalized) return null;

  if (MEDIUM_CATEGORY_LABELS_FR[value]) return value;

  for (const [key, label] of Object.entries(MEDIUM_CATEGORY_LABELS_FR)) {
    if (normalized === normalizeComparableText(label) || normalized === normalizeComparableText(key)) {
      return key;
    }
  }

  for (const rule of CANONICAL_MEDIUM_RULES) {
    if (rule.terms.some((term) => normalized.includes(normalizeComparableText(term)))) {
      return rule.category;
    }
  }

  if (/photo|photograph|tirage|lambda|c print|c-print|chromogenic|argentique|pigment/.test(normalized)) return "photography";
  if (/huile|oil|acrylique|acrylic|toile|canvas|peinture|painting|gouache/.test(normalized)) return "painting";
  if (/papier|paper|crayon|pencil|graphite|mine de plomb|encre|ink|fusain|charcoal|aquarelle|watercolor|pastel/.test(normalized)) return "work_on_paper";
  if (/bronze|sculpture|marbre|marble|resine|résine|stone|pierre/.test(normalized)) return "sculpture";
  if (/lithograph|lithographie|serigraph|sérigraphie|screenprint|gravure|etching|estampe|printmaking/.test(normalized)) return "print";
  if (/mixed media|technique mixte|collage|assemblage/.test(normalized)) return "mixed_media";
  if (/video|vidéo|film/.test(normalized)) return "film_video";
  if (/digital|numerique|numérique|nft|generative|software|code/.test(normalized)) return "digital";
  if (/ceramique|céramique|ceramic|porcelain|porcelaine/.test(normalized)) return "ceramics";
  if (/textile|tapisserie|tapestry|fabric|tissu|soie|silk/.test(normalized)) return "textile";
  if (/design|chair|chaise|table|lamp|lampe|furniture|mobilier/.test(normalized)) return "design";

  return null;
}

function normalizeFieldValueForSave(key, value, source = {}) {
  const raw = compactString(value);
  if (!raw) return null;

  if (key === "medium") {
    return canonicalMedium(raw) || raw;
  }

  if (key === "medium_category") {
    return inferMediumCategory(raw || source.medium || source.medium_clean) || raw;
  }

  if (key === "acquisition_type") return normalizeAcquisitionType(raw) || raw;
  if (key === "acquisition_date") return normalizeDate(raw) || raw;

  return raw;
}

function normalizeFieldValueForCompare(key, value) {
  if (value === null || value === undefined || value === "") return null;

  if (key === "dimensions") return normalizeDimensionsForCompare(value);
  if (key === "medium") return canonicalMedium(value) || normalizeComparableText(value);
  if (key === "medium_category") return inferMediumCategory(value) || normalizeComparableText(value);
  if (key === "acquisition_price" || key === "auction_hammer_price" || key === "auction_fees") {
    const n = normalizePrice(value);
    return n === null ? normalizeComparableText(value) : String(n);
  }
  if (key === "auction_sale_date" || key === "acquisition_date") return normalizeDate(value) || normalizeComparableText(value);
  if (key === "acquisition_type") return normalizeAcquisitionType(value) || normalizeComparableText(value);

  return normalizeComparableText(value);
}

function getArtworkFieldValue(artwork, key) {
  if (!artwork) return null;
  if (key === "acquisition_price") {
    return artwork.acquisition_price ?? artwork.price ?? artwork.acquisitionDetails?.purchasePrice ?? artwork.acquisitionDetails?.price;
  }
  if (key === "acquisition_date") {
    return artwork.acquisition_date ?? artwork.acquisitionDetails?.acquisition_date ?? artwork.acquisitionDetails?.date;
  }
  if (key === "acquisition_type") {
    return artwork.acquisition_type ?? artwork.acquisitionDetails?.acquisition_type ?? artwork.acquisitionDetails?.sale_type ?? artwork.acquisitionDetails?.type;
  }
  if (key === "seller") return artwork.acquisitionDetails?.seller ?? artwork.acquisitionDetails?.donor;
  if (key === "buyer") return artwork.acquisitionDetails?.buyer ?? artwork.acquisitionDetails?.exchange_counterparty;
  return artwork[key];
}

function valuesAreEquivalent(key, currentValue, nextValue) {
  const currentNorm = normalizeFieldValueForCompare(key, currentValue);
  const nextNorm = normalizeFieldValueForCompare(key, nextValue);
  if (!currentNorm || !nextNorm) return false;
  return currentNorm === nextNorm;
}

function displayOcrValue(key, value, t) {
  const raw = compactString(value);
  if (!raw) return "";

  if (key === "medium") {
    return t ? t(`artwork.mediums.${raw}`, { defaultValue: MEDIUM_LABELS_FR[raw] || raw }) : (MEDIUM_LABELS_FR[raw] || raw);
  }

  if (key === "medium_category") {
    return t ? t(`ocrEnrich.mediumCategory.${raw}`, { defaultValue: MEDIUM_CATEGORY_LABELS_FR[raw] || raw }) : (MEDIUM_CATEGORY_LABELS_FR[raw] || raw);
  }

  if (key === "acquisition_price" || key === "auction_hammer_price" || key === "auction_fees") {
    const n = normalizePrice(raw);
    return n !== null ? `${n.toLocaleString("fr-FR")} €` : raw;
  }

  return raw;
}

function normalizePrice(raw) {
  if (raw === null || raw === undefined || raw === "") return null;
  let s = String(raw).replace(/[€$£\s]/g, "").trim();
  if (/^\d{1,3}(\.\d{3})+(,\d{1,2})?(-)?$/.test(s)) {
    s = s.replace(/\./g, "").replace(",", ".").replace(/-$/, "");
  } else {
    s = s.replace(/\s/g, "").replace(",", ".").replace(/-$/, "");
  }
  const num = parseFloat(s);
  return isNaN(num) ? null : num;
}

function compactString(raw) {
  const value = raw === null || raw === undefined ? "" : String(raw).trim();
  return value || null;
}


function normalizeDate(raw) {
  const value = compactString(raw);
  if (!value) return null;
  if (/^\d{4}-\d{2}-\d{2}/.test(value)) return value.slice(0, 10);

  const numeric = value.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/);
  if (numeric) {
    const [, d, m, y] = numeric;
    return `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
  }

  const longFr = value.match(
    /(?:paris\s*,?\s*)?(?:le\s*)?(\d{1,2})\s+(janvier|février|fevrier|mars|avril|mai|juin|juillet|août|aout|septembre|octobre|novembre|décembre|decembre)\s+(\d{4})/i
  );
  if (longFr) {
    const [, d, month, y] = longFr;
    const mm = normalizeFrenchMonth(month);
    if (mm) return `${y}-${mm}-${String(d).padStart(2, "0")}`;
  }

  return value;
}

function normalizeFrenchMonth(month) {
  const m = normalizeComparableText(month);
  const months = {
    janvier: "01",
    fevrier: "02",
    mars: "03",
    avril: "04",
    mai: "05",
    juin: "06",
    juillet: "07",
    aout: "08",
    septembre: "09",
    octobre: "10",
    novembre: "11",
    decembre: "12",
  };
  return months[m] || null;
}

function normalizeAcquisitionType(raw) {
  const value = normalizeComparableText(raw);
  if (!value) return null;
  const aliases = {
    purchase: ["purchase", "achat", "buy", "acquisition", "achat autre"],
    artist: ["artist", "artist_sale", "vente artiste", "vente directe artiste", "artiste", "atelier", "studio"],
    gallery: ["gallery", "galerie", "dealer", "marchand"],
    auction: ["auction", "auction_house", "vente aux encheres", "encheres", "maison de vente", "bordereau", "adjudication", "adjuge", "lot", "frais ttc"],
    private: ["private", "private_sale", "vente privee", "particulier"],
    donation: ["donation", "acte de donation", "don", "gift", "schenkung"],
    exchange: ["exchange", "echange", "acte d echange", "contrepartie", "en contrepartie"],
    inheritance: ["inheritance", "heritage", "succession", "legs"],
    other: ["other", "autre"],
  };
  for (const [canonical, terms] of Object.entries(aliases)) {
    if (terms.some((term) => {
      const normalizedTerm = normalizeComparableText(term);
      return value === normalizedTerm || value.includes(normalizedTerm);
    })) return canonical;
  }
  return raw;
}

function buildSourceText(...sources) {
  return normalizeComparableText(
    sources
      .flatMap((source) => {
        if (!source) return [];
        if (typeof source === "string" || typeof source === "number") return [source];
        if (Array.isArray(source)) return source.map((item) => buildSourceText(item));
        if (typeof source === "object") return Object.values(source);
        return [];
      })
      .filter((v) => v !== null && v !== undefined)
      .join(" ")
  );
}

function hasDonationSignals(value) {
  const text = normalizeComparableText(value);
  return text.includes("acte de donation") || text.includes("donation") || text.includes("don en faveur") || text.includes("en faveur de");
}

function hasExchangeSignals(value) {
  const text = normalizeComparableText(value);
  return text.includes("acte d echange") || text.includes("acte d'echange") || text.includes("echange") || text.includes("contrepartie");
}

function extractValueFromText(value) {
  const raw = String(value || "");
  const match = raw.match(/valeur\s*[:-]?\s*([\d\s.,]+)\s*€/i) || raw.match(/pour une valeur de\s*([\d\s.,]+)\s*€/i);
  return match?.[1] ? normalizePrice(match[1]) : null;
}

function extractDocumentDateFromText(value) {
  const raw = String(value || "");

  const longFr = raw.match(
    /(?:[A-ZÀ-ÿ][A-Za-zÀ-ÿ\s-]+,\s*)?(?:le\s*)?(\d{1,2})\s+(janvier|février|fevrier|mars|avril|mai|juin|juillet|août|aout|septembre|octobre|novembre|décembre|decembre)\s+(\d{4})/i
  );

  if (longFr) {
    const [, d, month, y] = longFr;
    const mm = normalizeFrenchMonth(month);
    if (mm) return `${y}-${mm}-${String(d).padStart(2, "0")}`;
  }

  const numeric = raw.match(/(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})/);
  if (numeric) {
    const [, d, m, y] = numeric;
    return `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
  }

  return null;
}

function extractCounterpartyFromText(value) {
  const raw = String(value || "");
  const patterns = [
    /en faveur de\s+(?:la\s+)?([a-z0-9\s.'&-]+?)(?:\s+sans titre|\s+valeur|\s+pour le compte|$)/i,
    /pour le compte de\s+(?:la\s+)?([a-z0-9\s.'&-]+?)(?:\s+xiao|\s+valeur|$)/i,
    /a la galerie\s+([^,\n]+?)(?:\s+pour|\s+valeur|$)/i,
    /à la galerie\s+([^,\n]+?)(?:\s+pour|\s+valeur|$)/i,
  ];
  for (const pattern of patterns) {
    const match = raw.match(pattern);
    if (match?.[1]) {
      const cleaned = match[1].trim().replace(/\s+/g, " ");
      if (!cleaned) continue;
      return cleaned.toLowerCase().startsWith("galerie") ? cleaned : `Galerie ${cleaned}`;
    }
  }
  return null;
}

function looksLikeArtistSeller({ seller, artist }) {
  const s = normalizeComparableText(seller);
  const a = normalizeComparableText(artist);

  if (!s || !a) return false;

  const sellerTokens = s
    .split(" ")
    .filter((token) => token.length > 1);

  const artistTokens = a
    .split(" ")
    .filter((token) => token.length > 1);

  if (!sellerTokens.length || !artistTokens.length) return false;

  const common = artistTokens.filter((token) => sellerTokens.includes(token));

  // Cas XIAO FAN ↔ XIAO FAN RU : tous les tokens artiste sont dans le vendeur.
  if (common.length === artistTokens.length) return true;

  // Cas inverse ou nom partiel : au moins 2 tokens communs.
  if (common.length >= 2) return true;

  // Cas artiste mononyme / rare : un seul token long peut suffire.
  if (common.length === 1 && common[0].length >= 5) return true;

  return false;
}


function parseDimStr(str) {
  if (!str) return null;
  const m = String(str).match(/(\d+[.,]?\d*)\s*[x×]\s*(\d+[.,]?\d*)/i);
  if (!m) return null;
  return {
    h: parseFloat(String(m[1]).replace(",", ".")),
    w: parseFloat(String(m[2]).replace(",", ".")),
  };
}

// Détecte écart symétrique typique d'un cadre (2–25 cm de chaque côté)
function detectFrameGap(dimFiche, dimFacture) {
  if (!dimFiche || !dimFacture) return null;
  const dh = Math.abs(dimFiche.h - dimFacture.h);
  const dw = Math.abs(dimFiche.w - dimFacture.w);
  if (dh > 0 && dw > 0 && Math.abs(dh - dw) <= 2 && dh >= 2 && dh <= 25) {
    return { diff: Math.round(dh), fichePlus: dimFiche.h > dimFacture.h };
  }
  return null;
}


function getConfidenceBadge(score) {
  if (score >= 85) return { labelKey: "ocrEnrich.confidence.level.veryHigh", label: "Très élevé", color: "#16a34a", bg: "#f0fdf4", border: "#bbf7d0" };
  if (score >= 70) return { labelKey: "ocrEnrich.confidence.level.high", label: "Élevé", color: "#15803d", bg: "#f7fee7", border: "#d9f99d" };
  if (score >= 50) return { labelKey: "ocrEnrich.confidence.level.medium", label: "Moyen", color: "#a16207", bg: "#fffbeb", border: "#fde68a" };
  return { labelKey: "ocrEnrich.confidence.level.careful", label: "Prudent", color: "#b45309", bg: "#fff7ed", border: "#fdba74" };
}

function computeOcrConfidence({ useful, invoiceDoc, certDoc, matchedArtwork, invoiceData, certData, dimWarning }) {
  let score = 18;

  if (invoiceDoc) score += 14;
  if (certDoc) score += 10;
  if (matchedArtwork) score += 14;

  const weights = {
    artist: 12,
    title: 12,
    year: 10,
    medium: 10,
    dimensions: 14,
    acquisition_price: 12,
    signature_details: 6,
    authentication_details: 8,
    signature_notes: 4,
  };

  Object.keys(useful || {}).forEach((key) => {
    score += weights[key] || 4;
  });

  if (invoiceData?.artist && invoiceData?.title) score += 6;
  if (invoiceData?.dimensions) score += 4;
  if (invoiceData?.price) score += 4;
  if (certData?.certificate_reference || certData?.signature) score += 6;

  if (dimWarning) score -= dimWarning.isFrameGap ? 6 : 12;

  score = Math.max(0, Math.min(99, score));
  const badge = getConfidenceBadge(score);

  return {
    score,
    label: badge.label,
    labelKey: badge.labelKey,
    color: badge.color,
    bg: badge.bg,
    border: badge.border,
  };
}

// Récupère l'URL signée d'un document Supabase
async function getSignedUrl(doc) {
  const bucket = doc.bucket || "artworks-documents";
  if (doc.storagePath) {
    const { data: signed } = await supabase.storage
      .from(bucket)
      .createSignedUrl(doc.storagePath, 120);
    return signed?.signedUrl || null;
  }
  const rawUrl = doc.file || doc.url || "";
  if (rawUrl.includes("/storage/v1/object/public/")) {
    const marker = "/storage/v1/object/public/";
    const after = rawUrl.split(marker)[1].split("?")[0];
    const b = after.split("/")[0];
    const p = decodeURIComponent(after.slice(b.length + 1));
    const { data: signed } = await supabase.storage.from(b).createSignedUrl(p, 120);
    return signed?.signedUrl || null;
  }
  return rawUrl.startsWith("http") ? rawUrl : null;
}

// Convertit un document en base64
async function docToBase64(doc) {
  const url = await getSignedUrl(doc);
  if (!url) throw new Error("URL inaccessible : " + (doc.name || doc.storagePath));
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Lecture impossible (${res.status})`);
  const blob = await res.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result.split(",")[1]);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

// Appelle l'API OCR avec le bon hint de type
async function analyzeDoc(doc, token, clientId) {
  const b64 = await docToBase64(doc);
  if (!b64 || b64.length < 100) throw new Error("Document vide ou illisible.");
  const res = await fetch(OCR_API, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({
  file: b64,
  document_type: isAcquisitionLikeDocument(doc) ? "invoice" : doc.type,
  client_id: clientId || null,
}),
  });
  if (!res.ok) {
    const txt = await res.text().catch(() => "");
    throw new Error(`OCR API ${res.status} — ${txt.substring(0, 200)}`);
  }
  return res.json();
}

// ── Composant ──────────────────────────────────────────────────────────────

export default function OcrEnrichButton({ artwork, onEnriched }) {
  const { t } = useTranslation();
  const tr = (key, fallback, options = {}) => t(key, { defaultValue: fallback, ...options });
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState(false);
  const [extracted, setExtracted] = useState(null);
  const [selected, setSelected] = useState({});
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [dimWarning, setDimWarning] = useState(null);
  const [dimConfirm, setDimConfirm] = useState(null);
  const [confidence, setConfidence] = useState(null);
  const [localDocs, setLocalDocs] = useState(null); // docs avec types éventuellement corrigés par user
  const [openMenuDocId, setOpenMenuDocId] = useState(null); // id du doc dont le menu type est ouvert

  const allDocs = localDocs ?? parseDocuments(artwork?.documents);

  const hasFile = (d) => d.file || d.url || d.storagePath;
  const invoiceDoc = allDocs.find((d) => isAcquisitionLikeDocument(d) && hasFile(d));

  const certDoc = allDocs.find((d) => d.type === "certificate" && hasFile(d));
  const fallbackDoc = allDocs.find((d) => !IGNORED_TYPES.includes(d.type) && hasFile(d));

  if (!invoiceDoc && !certDoc && !fallbackDoc) return null;

  const DOC_TYPE_LABELS = {
    invoice: tr("ocrEnrich.documentTypes.invoice", "Facture / Bordereau / Acte donation ou échange"),
    certificate: tr("ocrEnrich.documentTypes.certificate", "Certificat d'authenticité"),
    condition_report: tr("ocrEnrich.documentTypes.condition_report", "Constat d'état"),
    exhibition: tr("ocrEnrich.documentTypes.exhibition", "Document d'exposition"),
    other: tr("ocrEnrich.documentTypes.other", "Autre"),
  };

  const handleReclassifyDoc = (docId, newType) => {
    setLocalDocs((prev) => {
      const base = prev ?? parseDocuments(artwork?.documents);
      return base.map((d) =>
        (d.id === docId || d.name === docId) ? { ...d, type: newType } : d
      );
    });
  };

  const handleAnalyze = async () => {
    setLoading(true);
    setDone(false);
    setDimWarning(null);
    setDimConfirm(null);
    setConfidence(null);

    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const token = session?.access_token;

      let invoiceData = {};
      let invoiceArtworks = [];
      if (invoiceDoc) {
        try {
          const r = await analyzeDoc(invoiceDoc, token, artwork?.client_id);
          invoiceData = r.extracted_data || {};
          invoiceArtworks = r.artworks || [];
        } catch (e) {
          console.warn("[OcrEnrich] Facture ignorée :", e.message);
        }
      }

      let certData = {};
      if (certDoc) {
        try {
          const r = await analyzeDoc(certDoc, token, artwork?.client_id);
          certData = r.extracted_data || {};
        } catch (e) {
          console.warn("[OcrEnrich] Certificat ignoré :", e.message);
        }
      }

      let fallbackData = {};
      let fallbackArtworks = [];
      if (!invoiceDoc && !certDoc && fallbackDoc) {
        try {
          const r = await analyzeDoc(fallbackDoc, token, artwork?.client_id);
          fallbackData = r.extracted_data || {};
          fallbackArtworks = r.artworks || [];
        } catch (e) {
          console.warn("[OcrEnrich] Fallback ignoré :", e.message);
        }
      }

      const artworksList = invoiceArtworks.length ? invoiceArtworks : fallbackArtworks;
      const baseData = Object.keys(invoiceData).length ? invoiceData : fallbackData;

let matchedArtwork = pickBestArtworkMatch(artworksList, artwork);

// Fallback seulement si le document ne contient qu’une seule œuvre.
// Surtout pas artworksList[0] quand il y a plusieurs œuvres.
if (!matchedArtwork && artworksList.length === 1) {
  matchedArtwork = artworksList[0];
}


      const data = { ...baseData, ...(matchedArtwork || {}) };

      // Normalisation OCR maison de vente : certaines infos sont globales
      // (extracted_data), d'autres sont parfois au niveau de l'œuvre détectée.
      const auctionSource = { ...invoiceData, ...baseData, ...(matchedArtwork || {}) };
      if (!data.price && auctionSource.total_amount) data.price = auctionSource.total_amount;
      if (!data.currency && auctionSource.currency) data.currency = auctionSource.currency;
      if (!data.auction_house && auctionSource.auction_house) data.auction_house = auctionSource.auction_house;
      if (!data.auction_sale_name && auctionSource.auction_sale_name) data.auction_sale_name = auctionSource.auction_sale_name;
      if (!data.auction_sale_date && auctionSource.auction_sale_date) data.auction_sale_date = auctionSource.auction_sale_date;
      if (!data.auction_lot_number && (auctionSource.auction_lot_number || auctionSource.lot_number)) {
        data.auction_lot_number = auctionSource.auction_lot_number || auctionSource.lot_number;
      }
      if (!data.auction_hammer_price && auctionSource.auction_hammer_price) {
        data.auction_hammer_price = auctionSource.auction_hammer_price;
      }
      if (!data.auction_fees && auctionSource.auction_fees) {
        data.auction_fees = auctionSource.auction_fees;
      }

      const sourceText = buildSourceText(invoiceData, baseData, matchedArtwork, invoiceDoc, fallbackDoc, artwork.documents);
      const donationDetected = hasDonationSignals(sourceText);
      const exchangeDetected = hasExchangeSignals(sourceText);
      const documentDate = extractDocumentDateFromText(sourceText);
      const declaredValue = extractValueFromText(sourceText);
      const counterparty = extractCounterpartyFromText(sourceText);

      if (exchangeDetected) {
        const artworkArtist = artwork.artist || data.artist;
        data.acquisition_type = "exchange";
        data.sale_type = "exchange";
        if (documentDate) data.acquisition_date = documentDate;
        if (declaredValue !== null) {
          data.price = declaredValue;
          data.acquisition_price = declaredValue;
        }
        if (looksLikeArtistSeller({ seller: sourceText, artist: artworkArtist })) {
          data.seller = artworkArtist;
          data.acquisition_source = artworkArtist;
        }
        if (!data.buyer && counterparty) data.buyer = counterparty;
        data.exchange_counterparty = data.buyer || counterparty || null;
      }

      if (donationDetected && !exchangeDetected) {
        const artworkArtist = artwork.artist || data.artist;
        data.acquisition_type = "donation";
        data.sale_type = "donation";
        if (documentDate) data.acquisition_date = documentDate;
        if (declaredValue !== null) {
          data.price = declaredValue;
          data.acquisition_price = declaredValue;
        }
        if (looksLikeArtistSeller({ seller: sourceText, artist: artworkArtist })) {
          data.seller = artworkArtist;
          data.donor = artworkArtist;
          data.acquisition_source = artworkArtist;
          data.donation_source_type = "artist";
        }
        if (!data.buyer && counterparty) data.buyer = counterparty;
      }

const artistSellerDetected = looksLikeArtistSeller({
  seller: data.seller || data.acquisition_source || sourceText,
  artist: artwork.artist || data.artist,
});

if (
  artistSellerDetected &&
  !["auction", "donation", "exchange"].includes(normalizeAcquisitionType(data.acquisition_type))
) {
  data.acquisition_type = "artist";
  data.sale_type = "artist";

  data.acquisition_source =
    data.seller ||
    data.acquisition_source ||
    artwork.artist ||
    data.artist;
}

      if (!data.category) {
        data.category = inferMediumCategory(data.medium_clean || data.medium);
      }

      const factureDimStr = matchedArtwork?.dimensions || invoiceData.dimensions || null;
      const ficheDimStr = artwork.dimensions;

      let localDimWarning = null;
      if (
        invoiceDoc &&
        factureDimStr &&
        ficheDimStr &&
        !valuesAreEquivalent("dimensions", ficheDimStr, factureDimStr)
      ) {
        const dimFacture = parseDimStr(factureDimStr);
        const dimFiche = parseDimStr(ficheDimStr);
        if (dimFacture && dimFiche) {
          const gap = detectFrameGap(dimFiche, dimFacture);
          localDimWarning = {
            ficheDim: ficheDimStr,
            factureDim: factureDimStr,
            diff: gap ? gap.diff : null,
            fichePlus: gap ? gap.fichePlus : null,
            isFrameGap: !!gap,
          };
          setDimWarning(localDimWarning);
          setDimConfirm("pending");
        }
      }

      const useful = {};
      for (const f of FIELDS) {
        const source = CERT_ONLY_FIELDS.includes(f.key) ? certData : data;
        let rawVal = source[f.ocr];

if (f.key === "acquisition_date" && !rawVal) {
  rawVal =
    source.acquisition_date ||
    source.date ||
    source.invoice_date ||
    source.document_date ||
    source.donation_date ||
    source.exchange_date ||
    source.auction_sale_date;
}
        const val = normalizeFieldValueForSave(f.key, rawVal, source);
        const currentValue = getArtworkFieldValue(artwork, f.key);

        if (val && !valuesAreEquivalent(f.key, currentValue, val)) {
          useful[f.key] = val;
        }
      }

      if (!Object.keys(useful).length && !localDimWarning) {
        toast.info(tr("ocrEnrich.alerts.noNewInfo", "L'OCR n'a pas trouvé de nouvelles informations dans ces documents."));
        return;
      }

      const nextSelected = Object.fromEntries(
        Object.keys(useful).map((k) => {
          if (k === "title") {
            return [k, !TECHNICAL_TERMS.some((t) => useful[k].toLowerCase().includes(t))];
          }
          if (k === "dimensions") return [k, false];
          return [k, true];
        })
      );

      const nextConfidence = computeOcrConfidence({
        useful,
        invoiceDoc,
        certDoc,
        matchedArtwork,
        invoiceData,
        certData,
        dimWarning: localDimWarning,
      });

      setExtracted(useful);
      setSelected(nextSelected);
      setConfidence(nextConfidence);
      setModal(true);
    } catch (e) {
      toast.error(tr("ocrEnrich.alerts.ocrError", "Erreur OCR : {{message}}", { message: e.message }));
    } finally {
      setLoading(false);
    }
  };

const handleSave = async () => {
  setSaving(true);

  try {
    const selectedExtracted = {};

    for (const [key, checked] of Object.entries(selected)) {
      if (!checked || extracted?.[key] === undefined || extracted?.[key] === null) continue;
      selectedExtracted[key] = extracted[key];
    }

    if (!Object.keys(selectedExtracted).length) {
      toast.warning(tr("ocrEnrich.alerts.noFieldSelected", "Aucun champ sélectionné."));
      return;
    }

    const patch = mapOcrToArtworkPatch({
      extracted: selectedExtracted,
      artwork,
    });

    if (!patch || !Object.keys(patch).length) {
      toast.warning(tr("ocrEnrich.alerts.noPatch", "Aucune donnée exploitable après normalisation."));
      return;
    }

    // Sécurité Supabase : jamais de colonnes fantômes
    delete patch.seller;
    delete patch.buyer;
    delete patch.donor;
    delete patch.exchange_counterparty;
    delete patch.counterparty;

    const { error } = await supabase
      .from("artworks")
      .update(patch)
      .eq("id", artwork.id);

    if (error) throw error;

    onEnriched?.({
      ...patch,
      _ocrConfidenceScore: confidence?.score ?? null,
      _ocrConfidenceLabel: confidence?.label ?? null,
    });

    setDone(true);
    setTimeout(() => {
      setModal(false);
      setDone(false);
    }, 1500);
  } catch (e) {
    toast.error(tr("ocrEnrich.alerts.saveError", "Erreur sauvegarde : {{message}}", { message: e.message }));
  } finally {
    setSaving(false);
  }
};

  const hasCheckedFields = extracted && Object.values(selected).some(Boolean);

  return (
    <>
      <button
        onClick={handleAnalyze}
        disabled={loading}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          padding: "8px 14px",
          background: "#fdf9f0",
          border: "1px solid #c9a961",
          borderRadius: 6,
          cursor: loading ? "not-allowed" : "pointer",
          fontSize: 13,
          fontFamily: "system-ui, sans-serif",
          fontWeight: 500,
          color: "#c9a961",
          transition: "all 0.15s",
        }}
        title={tr("ocrEnrich.button.title", "Extraire les métadonnées depuis les documents PDF de la fiche")}
      >
        {loading ? (
          <Loader2 size={14} style={{ animation: "spin 1s linear infinite" }} />
        ) : (
          <Sparkles size={14} />
        )}
        {loading ? tr("ocrEnrich.button.analyzing", "Analyse en cours…") : tr("ocrEnrich.button.enrich", "Enrichir via OCR")}
      </button>

      {modal && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.55)",
            zIndex: 50,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) setModal(false);
          }}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: 12,
              width: "min(540px, 100%)",
              maxHeight: "90vh",
              display: "flex",
              flexDirection: "column",
              fontFamily: "Georgia, serif",
            }}
          >
            <div
              style={{
                padding: "16px 20px",
                borderBottom: "1px solid #f0ede8",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div>
                <p
                  style={{
                    margin: 0,
                    fontSize: 16,
                    fontWeight: 400,
                    color: "#1a1a1a",
                  }}
                >
                  {tr("ocrEnrich.modal.title", "Champs extraits par l'OCR")}
                </p>
                <p
                  style={{
                    margin: 0,
                    fontSize: 12,
                    color: "#6b6b6b",
                    marginTop: 2,
                  }}
                >
                  {tr("ocrEnrich.modal.subtitle", "Sélectionnez les champs à appliquer à la fiche")}
                </p>
                <AiDisclosure type="extracted" compact style={{ marginTop: 8 }} />
              </div>
              <button
                onClick={() => setModal(false)}
                style={{ background: "none", border: "none", cursor: "pointer" }}
              >
                <X size={18} color="#6b6b6b" />
              </button>
            </div>

            <div style={{ overflowY: "auto", flex: 1, padding: 20 }}>
              {/* ── Sources analysées avec reclassification ── */}
              {(() => {
                const analyzedDocs = allDocs.filter((d) => hasFile(d) && !IGNORED_TYPES.includes(d.type));
                if (!analyzedDocs.length) return null;
                return (
                  <div style={{
                    border: "1px solid #e8e4de",
                    borderRadius: 8,
                    marginBottom: 16,
                    overflow: "hidden",
                  }}>
                    <div style={{
                      background: "#f9f7f4",
                      padding: "8px 14px",
                      fontSize: 10,
                      color: "#9a8c6e",
                      fontFamily: "system-ui",
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.08em",
                    }}>
                      {tr("ocrEnrich.sections.documentsAnalyzed", "Documents analysés")}
                    </div>
                    {analyzedDocs.map((doc, idx) => {
                      const docId = doc.id || doc.name;
                      return (
                        <div key={docId || idx} style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          padding: "9px 14px",
                          borderTop: idx > 0 ? "1px solid #f0ede8" : "none",
                          gap: 10,
                        }}>
                          <span style={{
                            fontSize: 13,
                            color: "#1a1a1a",
                            fontFamily: "system-ui",
                            flex: 1,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}>
                            {doc.name || doc.storagePath?.split("/").pop() || "Document"}
                          </span>
                          {/* Badge type + crayon */}
                          <div style={{ position: "relative", flexShrink: 0 }}>
                            <button
                              type="button"
                              onClick={() => setOpenMenuDocId((prev) => prev === docId ? null : docId)}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 5,
                                padding: "3px 8px",
                                background: doc.type === "invoice" ? "#fdf9f0" : doc.type === "certificate" ? "#f0f7f4" : "#f5f5f5",
                                border: `1px solid ${doc.type === "invoice" ? "#c9a961" : doc.type === "certificate" ? "#6aab8e" : "#d0ccc7"}`,
                                borderRadius: 99,
                                fontSize: 11,
                                color: doc.type === "invoice" ? "#8a6a20" : doc.type === "certificate" ? "#2a6e4e" : "#666",
                                cursor: "pointer",
                                fontFamily: "system-ui",
                              }}
                            >
                              {DOC_TYPE_LABELS[doc.type] || doc.type}
                              <svg width="11" height="11" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Z" />
                              </svg>
                            </button>
                            {openMenuDocId === docId && (
                              <div style={{
                                position: "absolute",
                                right: 0,
                                top: "calc(100% + 4px)",
                                background: "#fff",
                                border: "1px solid #e0ddd8",
                                borderRadius: 8,
                                boxShadow: "0 4px 16px rgba(0,0,0,0.10)",
                                zIndex: 100,
                                minWidth: 190,
                                overflow: "hidden",
                              }}>
                                <p style={{ fontSize: 10, color: "#9a8c6e", padding: "7px 12px 4px", margin: 0, textTransform: "uppercase", letterSpacing: "0.07em", fontFamily: "system-ui", fontWeight: 700 }}>
                                  {tr("ocrEnrich.documents.changeCategory", "Changer la catégorie")}
                                </p>
                                {Object.entries(DOC_TYPE_LABELS).map(([typeKey, typeLabel]) => (
                                  <button
                                    key={typeKey}
                                    type="button"
                                    onClick={() => {
                                      handleReclassifyDoc(docId, typeKey);
                                      setOpenMenuDocId(null);
                                    }}
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "space-between",
                                      width: "100%",
                                      padding: "8px 12px",
                                      border: "none",
                                      background: doc.type === typeKey ? "#fdf9f0" : "transparent",
                                      color: doc.type === typeKey ? "#c9a961" : "#1a1a1a",
                                      fontSize: 13,
                                      fontFamily: "system-ui",
                                      cursor: "pointer",
                                      textAlign: "left",
                                    }}
                                  >
                                    {typeLabel}
                                    {doc.type === typeKey && (
                                      <svg width="13" height="13" fill="none" viewBox="0 0 24 24" stroke="#c9a961" strokeWidth={2.5}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                                      </svg>
                                    )}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                    {/* Bouton relancer si type modifié */}
                    {localDocs && (
                      <div style={{ padding: "10px 14px", borderTop: "1px solid #f0ede8", background: "#fffbf2" }}>
                        <button
                          type="button"
                          onClick={() => { setExtracted(null); setSelected({}); setConfidence(null); handleAnalyze(); }}
                          style={{
                            width: "100%",
                            padding: "7px 0",
                            background: "#1a1a1a",
                            color: "#fff",
                            border: "none",
                            borderRadius: 6,
                            fontSize: 12,
                            fontFamily: "Georgia, serif",
                            cursor: "pointer",
                            letterSpacing: "0.04em",
                          }}
                        >
                          {tr("ocrEnrich.documents.rerun", "↺ Relancer l'analyse avec les types corrigés")}
                        </button>
                      </div>
                    )}
                  </div>
                );
              })()}

              {confidence && (
                <div
                  style={{
                    background: confidence.bg,
                    border: `1px solid ${confidence.border}`,
                    borderRadius: 8,
                    padding: "12px 14px",
                    marginBottom: 16,
                    fontFamily: "system-ui, sans-serif",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 12,
                      flexWrap: "wrap",
                    }}
                  >
                    <div>
                      <div style={{ fontSize: 12, color: "#6b6b6b", textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 700 }}>
                        {tr("ocrEnrich.confidence.title", "Score de confiance OCR")}
                      </div>
                      <div style={{ fontSize: 18, fontWeight: 700, color: confidence.color }}>
                        {confidence.score}/99
                      </div>
                    </div>
                    <div
                      style={{
                        padding: "6px 10px",
                        borderRadius: 999,
                        background: "#fff",
                        color: confidence.color,
                        border: `1px solid ${confidence.border}`,
                        fontSize: 12,
                        fontWeight: 700,
                      }}
                    >
                      {tr(confidence.labelKey || "ocrEnrich.confidence.level.careful", confidence.label)}
                    </div>
                  </div>
                  <p style={{ margin: "8px 0 0", fontSize: 12, color: "#6b6b6b", lineHeight: 1.5 }}>
                    {tr("ocrEnrich.confidence.description", "Le score combine la qualité des sources trouvées, le matching facture/œuvre et les éventuelles incohérences détectées.")}
                  </p>
                </div>
              )}

              {dimWarning && (
                <div
                  style={{
                    background: "#fffbf0",
                    border: "1px solid #c9a961",
                    borderRadius: 8,
                    padding: "14px 16px",
                    marginBottom: 16,
                  }}
                >
                  <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                    <AlertTriangle
                      size={16}
                      color="#c9a961"
                      style={{ marginTop: 2, flexShrink: 0 }}
                    />
                    <div
                      style={{
                        fontSize: 13,
                        color: "#5a4010",
                        fontFamily: "system-ui",
                        lineHeight: 1.6,
                      }}
                    >
                      <strong>{tr("ocrEnrich.dimensions.warningTitle", "Écart de dimensions détecté")}</strong>
                      <br />
                      <span style={{ color: "#888" }}>{tr("ocrEnrich.dimensions.record", "Fiche :")}</span>{" "}
                      <strong>{dimWarning.ficheDim}</strong> {"  ·  "}
                      <span style={{ color: "#888" }}>{tr("ocrEnrich.dimensions.document", "Facture :")}</span>{" "}
                      <strong>{dimWarning.factureDim}</strong>
                      <br />
                      {dimWarning.isFrameGap ? (
                        <span style={{ fontSize: 12, color: "#7a6030" }}>
                          Différence de <strong>{dimWarning.diff} cm</strong> de chaque côté —{" "}
                          {dimWarning.fichePlus
                            ? "la fiche inclut probablement le cadre (mesure hors-tout)."
                            : "la facture inclut probablement le cadre."}
                        </span>
                      ) : (
                        <span style={{ fontSize: 12, color: "#7a6030" }}>
                          Les dimensions ne correspondent pas — à vérifier avant d'appliquer.
                        </span>
                      )}
                    </div>
                  </div>

                  {dimConfirm === "pending" && (
                    <div
                      style={{
                        marginTop: 12,
                        paddingTop: 12,
                        borderTop: "1px solid #e8d9b0",
                      }}
                    >
                      <p
                        style={{
                          margin: "0 0 8px",
                          fontSize: 12,
                          fontFamily: "system-ui",
                          color: "#5a4010",
                          fontWeight: 600,
                        }}
                      >
                        {tr("ocrEnrich.dimensions.applyQuestion", "Appliquer les dimensions de la facture à la fiche ?")}
                      </p>
                      <div style={{ display: "flex", gap: 8 }}>
                        <button
                          onClick={() => {
                            setDimConfirm("confirmed");
                            setSelected((s) => ({ ...s, dimensions: true }));
                          }}
                          style={{
                            flex: 1,
                            padding: "8px",
                            fontSize: 12,
                            fontFamily: "system-ui",
                            fontWeight: 600,
                            background: "#1a1a1a",
                            color: "#fff",
                            border: "none",
                            borderRadius: 6,
                            cursor: "pointer",
                          }}
                        >
                          {tr("ocrEnrich.dimensions.yesApply", "Oui — appliquer {{dimensions}}", { dimensions: dimWarning.factureDim })}
                        </button>
                        <button
                          onClick={() => {
                            setDimConfirm("rejected");
                            setSelected((s) => ({ ...s, dimensions: false }));
                          }}
                          style={{
                            flex: 1,
                            padding: "8px",
                            fontSize: 12,
                            fontFamily: "system-ui",
                            background: "transparent",
                            color: "#5a4010",
                            border: "1px solid #c9a961",
                            borderRadius: 6,
                            cursor: "pointer",
                          }}
                        >
                          {tr("ocrEnrich.dimensions.noKeep", "Non — garder {{dimensions}}", { dimensions: dimWarning.ficheDim })}
                        </button>
                      </div>
                    </div>
                  )}

                  {dimConfirm === "confirmed" && (
                    <p
                      style={{
                        margin: "10px 0 0",
                        fontSize: 12,
                        fontFamily: "system-ui",
                        color: "#16a34a",
                        fontWeight: 600,
                      }}
                    >
                      ✓ {tr("ocrEnrich.dimensions.confirmed", "Les dimensions de la facture seront appliquées")}
                    </p>
                  )}
                  {dimConfirm === "rejected" && (
                    <p
                      style={{
                        margin: "10px 0 0",
                        fontSize: 12,
                        fontFamily: "system-ui",
                        color: "#888",
                      }}
                    >
                      {tr("ocrEnrich.dimensions.rejected", "Dimensions inchangées — à vérifier manuellement")}
                    </p>
                  )}
                </div>
              )}

              <div style={{ display: "grid", gap: 10 }}>
                {/* Séparateur groupe maison de vente */}
                {extracted && FIELDS.filter((f) => f.group === "auction" && extracted[f.key]).length > 0 && (
                  <div style={{
                    gridColumn: "1 / -1",
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    margin: "4px 0 2px",
                  }}>
                    <div style={{ flex: 1, height: 1, background: "#e5e3df" }} />
                    <span style={{
                      fontSize: 10,
                      fontFamily: "Georgia, serif",
                      color: "#9a8c6e",
                      textTransform: "uppercase",
                      letterSpacing: "0.1em",
                      whiteSpace: "nowrap",
                    }}>
                      {tr("ocrEnrich.sections.auction", "Vente aux enchères")}
                    </span>
                    <div style={{ flex: 1, height: 1, background: "#e5e3df" }} />
                  </div>
                )}
                {extracted &&
                  FIELDS.filter((f) => extracted[f.key]).map((f) => {
                    const isPendingDim = f.key === "dimensions" && dimConfirm === "pending";
                    return (
                      <label
                        key={f.key}
                        style={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 12,
                          padding: "12px 14px",
                          background: selected[f.key] ? "#fdf9f0" : "#fafaf8",
                          border: `1px solid ${selected[f.key] ? "#c9a961" : "#e0ddd8"}`,
                          borderRadius: 8,
                          cursor: isPendingDim ? "default" : "pointer",
                          opacity: isPendingDim ? 0.4 : 1,
                          transition: "all 0.15s",
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={!!selected[f.key]}
                          disabled={isPendingDim}
                          onChange={(e) =>
                            setSelected((s) => ({ ...s, [f.key]: e.target.checked }))
                          }
                          style={{
                            marginTop: 2,
                            accentColor: "#c9a961",
                            width: 16,
                            height: 16,
                            cursor: isPendingDim ? "default" : "pointer",
                          }}
                        />
                        <div style={{ flex: 1 }}>
                          <p
                            style={{
                              margin: 0,
                              fontSize: 11,
                              color: "#6b6b6b",
                              fontFamily: "system-ui",
                              fontWeight: 600,
                              textTransform: "uppercase",
                              letterSpacing: "0.05em",
                            }}
                          >
                            {tr(f.labelKey, f.fallbackLabel)}
                            {isPendingDim && (
                              <span
                                style={{
                                  marginLeft: 6,
                                  color: "#c9a961",
                                  fontWeight: 400,
                                  textTransform: "none",
                                  fontSize: 10,
                                }}
                              >
                                {tr("ocrEnrich.dimensions.confirmationRequired", "⚠ confirmation requise ci-dessus")}
                              </span>
                            )}
                            {f.key === "title" &&
                              extracted[f.key] &&
                              TECHNICAL_TERMS.some((t) =>
                                extracted[f.key].toLowerCase().includes(t)
                              ) && (
                                <span
                                  style={{
                                    marginLeft: 6,
                                    color: "#c9a961",
                                    fontWeight: 400,
                                    textTransform: "none",
                                    fontSize: 10,
                                  }}
                                >
                                  {tr("ocrEnrich.titleTechnicalWarning", "⚠ description technique détectée")}
                                </span>
                              )}
                          </p>
                          <div
                            style={{
                              display: "flex",
                              gap: 8,
                              marginTop: 4,
                              alignItems: "center",
                              flexWrap: "wrap",
                            }}
                          >
                            {getArtworkFieldValue(artwork, f.key) && (
                              <>
                                <span
                                  style={{
                                    fontSize: 13,
                                    color: "#9b9b9b",
                                    textDecoration: "line-through",
                                  }}
                                >
                                  {displayOcrValue(f.key, getArtworkFieldValue(artwork, f.key), t)}
                                </span>
                                <span style={{ fontSize: 11, color: "#6b6b6b" }}>→</span>
                              </>
                            )}
                            <span
                              style={{
                                fontSize: 14,
                                fontWeight: 500,
                                color: "#1a1a1a",
                              }}
                            >
                              {displayOcrValue(f.key, extracted[f.key], t)}
                            </span>
                          </div>
                        </div>
                      </label>
                    );
                  })}

                {extracted && Object.keys(extracted).length === 0 && dimWarning && (
                  <p
                    style={{
                      fontSize: 13,
                      color: "#888",
                      fontFamily: "system-ui",
                      textAlign: "center",
                      padding: "8px 0",
                    }}
                  >
                    {tr("ocrEnrich.dimensions.onlyWarning", "Aucun autre champ à mettre à jour — vérifiez l'écart de dimensions ci-dessus.")}
                  </p>
                )}
              </div>
            </div>

            <div style={{ padding: "14px 20px", borderTop: "1px solid #f0ede8" }}>
              <button
                onClick={handleSave}
                disabled={saving || !hasCheckedFields}
                style={{
                  width: "100%",
                  padding: "12px",
                  background: done ? "#16a34a" : hasCheckedFields ? "#1a1a1a" : "#ccc",
                  color: "#fff",
                  border: "none",
                  borderRadius: 8,
                  fontSize: 15,
                  fontFamily: "Georgia, serif",
                  cursor: saving || !hasCheckedFields ? "not-allowed" : "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                  transition: "background 0.3s",
                }}
              >
                {saving ? (
                  <>
                    <Loader2 size={16} /> {tr("ocrEnrich.actions.saving", "Sauvegarde…")}
                  </>
                ) : done ? (
                  <>
                    <Check size={16} /> {tr("ocrEnrich.actions.applied", "Appliqué")}
                  </>
                ) : (
                  <>
                    {tr("ocrEnrich.actions.applyCount", "Appliquer {{count}} champ{{plural}}", {
                      count: Object.values(selected).filter(Boolean).length,
                      plural: Object.values(selected).filter(Boolean).length > 1 ? "s" : "",
                    })}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
